import {createRouter, createWebHistory} from 'vue-router'
import AnalyticsPage from "controllers/vue_components/analytics/AnalyticsPage"
import EventsPage from "controllers/vue_components/events/EventsPage"
import EventBookingsPage from "controllers/vue_components/events/bookings/EventBookingsPage"
import EventAttendeesPage from "controllers/vue_components/events/attendees/EventAttendeesPage"
import NewEvent from "controllers/vue_components/events/new_event"
import Availability from "controllers/vue_components/calendar/availability"
import Holiday from "controllers/vue_components/holiday"
import SettingsIndex from "controllers/vue_components/settings/v1"
import OrganizationStructure from "controllers/vue_components/organizations/structure/index.vue"
import OrganizationApplications from "controllers/vue_components/organizations/applications/index.vue"
import OrganizationApplicationFactsheet from "controllers/vue_components/organizations/applications/factsheet.vue"
import OrganizationItComponents from "controllers/vue_components/organizations/it_components/index.vue"
import OrganizationDashboard from "controllers/vue_components/organizations/dashboard"
import OrganizationEvents from "controllers/vue_components/organizations/events"
import OrganizationBookings from "controllers/vue_components/organizations/bookings"
import OrganizationCalendar from "controllers/vue_components/organizations/calendar"
import OrganizationAnalytics from "controllers/vue_components/organizations/analytics"
import OrganizationNotification from "controllers/vue_components/organizations/notification"
import OrganizationProfile from "controllers/vue_components/organizations/settings/profile"
import OrganizationSocialLinks from "controllers/vue_components/organizations/settings/social_links"
import OrganizationMembers from "controllers/vue_components/organizations/settings/members"
import OrganizationActivityLogs from "controllers/vue_components/organizations/activity_logs"
import OrganizationPlan from "controllers/vue_components/organizations/settings/plan"
import OrganizationDevelopers from "controllers/vue_components/organizations/settings/developers"
import OrganizationAccount from "controllers/vue_components/organizations/settings/account"
import OrganizationAvailabilities from "controllers/vue_components/organizations/availabilities"
import ConnectedCalendars from "controllers/vue_components/connected_calendars"
import IntegrationsPage from "controllers/vue_components/integrations/IntegrationsPage"
import WebhooksPage from "controllers/vue_components/integrations/WebhooksPage"
import ExternalPaymentsPage from "controllers/vue_components/integrations/ExternalPaymentsPage"
import QuestionsPage from "controllers/vue_components/questions/QuestionsPage"
import AttendeesList from "controllers/vue_components/events/booking/attendees/list"
import NotificationsPage from "controllers/vue_components/notifications/NotificationsPage"
import TasksPage from "controllers/vue_components/tasks/TasksPage.vue"
import TaskDetailPage from "controllers/vue_components/tasks/TaskDetailPage.vue"

import Individuals from "controllers/vue_components/pricing/individuals"
import TeamsAndOrganizations from "controllers/vue_components/pricing/teams_and_org"
import BuildWithTimelydo from "controllers/vue_components/pricing/build_with_timelydo"
import UserInvitationsPage from "controllers/vue_components/admin/user_invitations/UserInvitationsPage.vue"
import GlobalComponentsPage from "controllers/vue_components/admin/global_components/GlobalComponentsPage.vue"
import ReleaseNotesPage from "controllers/vue_components/admin/release_notes/ReleaseNotesPage.vue"
import CareerJobsPage from "controllers/vue_components/admin/career_jobs/CareerJobsPage.vue"
import { pageTitle } from 'utils/pageTitle.js'
import CalendarPage from "controllers/vue_components/calendar/CalendarPage.vue"

const routes = [
    {
        path: '/calendar',
        name: 'Calendar',
        component: CalendarPage,
        meta: { title: 'Calendar' },
    },
    {
        // /analytics is a section index only. Bookings is its landing page.
        path: '/analytics',
        redirect: '/analytics/bookings',
    },
    {
        path: '/analytics/bookings',
        name: 'AnalyticsBookings',
        component: AnalyticsPage,
        meta: { title: 'Bookings | Analytics' },
    },
    {
        path: '/analytics/tasks',
        name: 'AnalyticsTasks',
        component: AnalyticsPage,
        meta: { title: 'Tasks | Analytics' },
    },
    {
        path: '/analytics/traffic',
        name: 'AnalyticsTraffic',
        component: AnalyticsPage,
        meta: { title: 'Traffic | Analytics' },
    },
    {
        path: '/notifications',
        name: 'Notifications',
        component: NotificationsPage,
        meta: { title: 'Notifications' },
    },
    {
        path: '/notifications/visits',
        name: 'NotificationVisits',
        component: NotificationsPage,
        meta: { title: 'Visits | Notifications' },
    },
    {
        path: '/notifications/messages',
        name: 'NotificationMessages',
        component: NotificationsPage,
        meta: { title: 'Messages | Notifications' },
    },
    {
        path: '/connected_calendars',
        name: 'ConnectedCalendars',
        component: ConnectedCalendars,
        meta: { title: 'Connected Calendars' },
    },
    {
        path: '/integrations',
        name: 'Integrations',
        component: IntegrationsPage,
        meta: { title: 'Integrations' },
    },
    {
        path: '/integrations/webhooks',
        name: 'Webhooks',
        component: WebhooksPage,
        meta: { title: 'Webhooks | Integrations' },
    },
    {
        path: '/integrations/external_payments',
        name: 'ExternalPayments',
        component: ExternalPaymentsPage,
        meta: { title: 'External Payments | Integrations' },
    },
    {
        path: '/questions',
        name: 'Questions',
        component: QuestionsPage,
        meta: { title: 'Form Questions' },
    },
    {
        path: '/attendees',
        name: 'Attendees',
        component: AttendeesList,
        meta: { title: 'Attendees' },
    },
    {
        path: '/availabilities',
        name: 'Availability',
        component: Availability,
        meta: { title: 'Availabilities' },
    },
    {
        path: '/holidays',
        name: 'Holidays',
        component: Holiday,
        meta: { title: 'Holidays' },
    },
    {
        path: '/tasks',
        name: 'Tasks',
        component: TasksPage,
        meta: { title: 'Tasks' },
        // The Stats tab moved to /analytics/tasks. Old links keep every filter
        // they carried; only the `tab` key is dropped. Rails redirects the same
        // shape for a cold load, this covers SPA navigation.
        beforeEnter: (to) => {
            if (to.query.tab !== 'stats') return
            const { tab, scope, ...filters } = to.query
            return { path: '/analytics/tasks', query: filters }
        },
    },
    {
        path: '/tasks/:id',
        name: 'TaskDetail',
        component: TaskDetailPage,
        meta: { title: 'Task | Tasks' },
    },
    {
        path: '/events',
        name: 'Events',
        component: EventsPage,
        meta: { title: 'Events' },
    },
    {
        path: '/events/new',
        name: 'NewEvent',
        component: NewEvent,
        meta: { title: 'New Event | Events' },
    },
    {
        path: '/events/:id/edit',
        name: 'EditEvent',
        component: NewEvent,
        meta: { title: 'Edit Event | Events' },
    },
    {
        path: '/events/:id/bookings',
        name: 'EventBookings',
        component: EventBookingsPage,
        meta: { title: 'Bookings | Events' },
    },
    {
        path: '/events/:id/attendees',
        name: 'EventAttendees',
        component: EventAttendeesPage,
        meta: { title: 'Attendees | Events' },
    },
    { path: '/settings/profile/general',      component: SettingsIndex, meta: { title: 'General | Settings' } },
    { path: '/settings/profile/branding',     component: SettingsIndex, meta: { title: 'Branding | Settings' } },
    { path: '/settings/profile/themes',       component: SettingsIndex, meta: { title: 'Themes | Settings' } },
    { path: '/settings/profile/social_links', component: SettingsIndex, meta: { title: 'Social Links | Settings' } },
    { path: '/settings/profile/policy',       component: SettingsIndex, meta: { title: 'Custom Policy | Settings' } },
    { path: '/settings/profile/verification', component: SettingsIndex, meta: { title: 'Get Verified | Settings' } },
    { path: '/settings/account',              component: SettingsIndex, meta: { title: 'Account | Settings' } },
    { path: '/settings/appearance',           component: SettingsIndex, meta: { title: 'Appearance | Settings' } },
    { path: '/settings/security',             component: SettingsIndex, meta: { title: 'Security | Settings' } },
    { path: '/settings/developers/api_key',   component: SettingsIndex, meta: { title: 'API Key | Settings' } },
    { path: '/settings/developers/embed_code',component: SettingsIndex, meta: { title: 'Embed Code | Settings' } },
    { path: '/settings/developers/qr_code',   component: SettingsIndex, meta: { title: 'QR Code | Settings' } },
    { path: '/settings/:section(.*)*', name: 'Settings', component: SettingsIndex, meta: { title: 'Settings' } },
    // ORGANIZATION ROUTES

    {
        path: '/organizations/:url/',
        children: [
            {
                path: 'structure',
                name: 'OrganizationStructure',
                component: OrganizationStructure,
                meta: { title: 'Structure | Organization' },
            },

            {
                path: 'applications',
                name: 'OrganizationApplications',
                component: OrganizationApplications,
                meta: { title: 'Applications | Organization' },
            },

            {
                path: 'applications/:id',
                name: 'OrganizationApplicationFactsheet',
                component: OrganizationApplicationFactsheet,
                meta: { title: 'Application Factsheet' },
            },

            {
                path: 'it_components',
                name: 'OrganizationItComponents',
                component: OrganizationItComponents,
                meta: { title: 'IT Components | Organization' },
            },

            {
                path: 'profile',
                component: OrganizationProfile,
                meta: { title: 'Profile | Organization' },
            },

            {
                path: 'profile/social_links',
                component: OrganizationSocialLinks,
                meta: { title: 'Social Links | Organization' },
            },

            {
                path: 'account',
                component: OrganizationAccount,
                meta: { title: 'Account | Organization' },
            },

            {
                path: 'members',
                component: OrganizationMembers,
                meta: { title: 'Members | Organization' },
            },

            {
                path: 'plan',
                component: OrganizationPlan,
                meta: { title: 'Plan | Organization' },
            },
        
            {
                path: 'developers',
                component: OrganizationDevelopers,
                meta: { title: 'Developers | Organization' },
            },

            {
                path: 'dashboard',
                name: 'OrganizationDashboard',
                component: OrganizationDashboard,
                meta: { title: 'Dashboard | Organization' }
            },

            {
                path: 'activity_logs',
                name: 'OrganizationActivityLogs',
                component: OrganizationActivityLogs,
                meta: { title: 'Activity Logs | Organization' },
            },

            {
                path: 'events',
                name: 'OrganizationEvents',
                component: OrganizationEvents,
                meta: { title: 'Events | Organization' }
            },
        
            {
                path: 'bookings',
                name: 'OrganizationBookings',
                component: OrganizationBookings,
                meta: { title: 'Bookings | Organization' }
            },
        
            {
                path: 'calendar',
                name: 'OrganizationCalendar',
                component: OrganizationCalendar,
                meta: { title: 'Calendar | Organization' }
            },
        
            {
                path: 'analytics',
                name: 'OrganizationAnalytics',
                component: OrganizationAnalytics,
                meta: { title: 'Analytics | Organization' }
            },
        
            {
                path: 'notifications',
                name: 'OrganizationNotification',
                component: OrganizationNotification,
                meta: { title: 'Notifications | Organization' }
            },
        
            {
                path: 'availabilities',
                name: 'OrganizationAvailabilities',
                component: OrganizationAvailabilities,
                meta: { title: 'Availabilities | Organization' }
            },
        ],
    },

    // Pricing routes

    {
        path: '/pricing/individuals',
        name: 'Individuals',
        component: Individuals,
        meta: { title: 'Individuals | Pricing' },
    },

    {
        path: '/pricing/teams-and-organizations',
        name: 'TeamsAndOrganizations',
        component: TeamsAndOrganizations,
        meta: { title: 'Teams And Organizations | Pricing' },
    },

    {
        path: '/pricing/build-with-timelydo',
        name: 'BuildWithTimelydo',
        component: BuildWithTimelydo,
        meta: { title: 'Build With Timelydo | Pricing' },
    },
    {
        path: '/admin/user_invitations',
        name: 'AdminUserInvitations',
        component: UserInvitationsPage,
        meta: { title: 'User Invitations | Admin' },
    },
    {
        path: '/admin/global_components',
        name: 'AdminGlobalComponents',
        component: GlobalComponentsPage,
        meta: { title: 'Global IT Components | Admin' },
    },
    {
        path: '/admin/release_notes',
        name: 'AdminReleaseNotes',
        component: ReleaseNotesPage,
        meta: { title: 'Release Notes | Admin' },
    },
    {
        path: '/admin/career_jobs',
        name: 'AdminCareerJobs',
        component: CareerJobsPage,
        meta: { title: 'Career Jobs | Admin' },
    },

    // Catch-all: Rails handles all other routes — render nothing and suppress the no-match warning
    { path: '/:pathMatch(.*)*', component: { render: () => null } },
]

const router = createRouter({
    history: createWebHistory('/'),
    routes,
})

// `meta.title` holds the trail only ("Bookings | Analytics"); the brand suffix
// is appended here so it lives in one place rather than in every route.
router.beforeEach((to, from, next) => {
    if (to.meta.title !== undefined) {
        document.title = pageTitle(to.meta.title)
    }

    next();
});

export default router